/**
 * Velocity Model Extraction
 *
 * The generator produces a complete Velocity configuration (the importable
 * JSON) alongside the GeoEvent-centric analysis. That configuration already
 * contains everything a user needs to recreate their workflow in Velocity:
 * feed properties, analytic tool parameters, Arcade expressions, data sources,
 * outputs, and the pipeline connectivity.
 *
 * Historically the migration guide only mined a tiny slice of this (the Arcade
 * migration notes). This module parses the generated Velocity configuration
 * into typed, queryable structures so the guide can surface the Velocity-side
 * configuration directly — both as per-component parameter tables and as a
 * Velocity-styled flow diagram.
 */

import type {
  VelocityConfiguration,
  VelocityFeedItem,
  VelocityRealtimeAnalytic,
  AnalyticFeedRef,
  AnalyticSource,
  AnalyticTool,
  AnalyticOutput,
} from '../types/velocity.js';
import type { DocumentTable, DocumentTableRow } from '../types/generator.js';
import { sanitizeVelocityLabel } from '../generator/velocity-label.js';
import velocityParamLabelsRaw from '../mapper/velocity-parameter-labels.json';

/** Lookup from full Velocity property key to a human-readable label. */
const velocityParamLabels: Record<string, string> = velocityParamLabelsRaw as Record<string, string>;

/** Property keys that are layout/runtime scaffolding, never shown to users. */
const SKIP_KEYS = new Set(['ui', 'arcadeSampleValues']);

/** A normalized view of a single Velocity component within an analytic. */
export interface VelocityComponentView {
  /** Which part of the Velocity model this component belongs to. */
  kind: 'feed' | 'source' | 'tool' | 'output';
  /** The Velocity component/tool name, e.g. `field-calculator`, `feature-layer`. */
  velocityName: string;
  /** The sanitized Velocity label as it will appear in the Velocity Manager. */
  label: string;
  /** The component's configuration properties (already merged for feeds). */
  properties: Record<string, unknown>;
}

/** The parsed Velocity configuration, indexed for correlation with GeoEvent components. */
export interface VelocityModel {
  /** All feed items keyed by their generated id. */
  feedsById: Map<string, VelocityFeedItem>;
  /** All real-time analytics in generation order. */
  analytics: VelocityRealtimeAnalytic[];
  /** Real-time analytics keyed by their sanitized label (matches the sanitized service label). */
  analyticsByLabel: Map<string, VelocityRealtimeAnalytic>;
}

/**
 * Parses a generated Velocity configuration into a queryable model.
 * Malformed JSON entries are skipped defensively so a single bad record
 * never breaks guide generation.
 */
export function buildVelocityModel(velocityConfig: VelocityConfiguration | undefined): VelocityModel {
  const feedsById = new Map<string, VelocityFeedItem>();
  const analytics: VelocityRealtimeAnalytic[] = [];
  const analyticsByLabel = new Map<string, VelocityRealtimeAnalytic>();

  if (!velocityConfig) {
    return { feedsById, analytics, analyticsByLabel };
  }

  for (const feedJson of velocityConfig.feeds || []) {
    try {
      const feed = JSON.parse(feedJson) as VelocityFeedItem;
      if (feed && typeof feed.id === 'string') {
        feedsById.set(feed.id, feed);
      }
    } catch {
      // Skip malformed feed entries.
    }
  }

  for (const realtimeJson of velocityConfig.realtime || []) {
    try {
      const analytic = JSON.parse(realtimeJson) as VelocityRealtimeAnalytic;
      if (!analytic || typeof analytic.label !== 'string') continue;
      analytics.push(analytic);
      if (!analyticsByLabel.has(analytic.label)) {
        analyticsByLabel.set(analytic.label, analytic);
      }
    } catch {
      // Skip malformed analytic entries.
    }
  }

  return { feedsById, analytics, analyticsByLabel };
}

/**
 * Finds the Velocity real-time analytic generated for a given GeoEvent service.
 * Correlation is by sanitized label, which is exactly how the generator names
 * the analytic (`sanitizeVelocityLabel(service.label || service.name)`).
 */
export function getAnalyticForService(
  model: VelocityModel,
  service: { name: string; label?: string },
): VelocityRealtimeAnalytic | undefined {
  const key = sanitizeVelocityLabel(service.label || service.name).sanitized;
  return model.analyticsByLabel.get(key);
}

/**
 * Returns every Velocity component in an analytic as a normalized view,
 * resolving feed references to their full feed configuration.
 */
export function listVelocityComponents(
  model: VelocityModel,
  analytic: VelocityRealtimeAnalytic,
): VelocityComponentView[] {
  const views: VelocityComponentView[] = [];

  for (const feedRef of analytic.feeds || []) {
    views.push(feedRefToView(model, feedRef));
  }
  for (const source of analytic.sources || []) {
    views.push(sourceToView(source));
  }
  for (const tool of analytic.tools || []) {
    views.push(toolToView(tool));
  }
  for (const output of analytic.outputs || []) {
    views.push(outputToView(output));
  }

  return views;
}

/**
 * Finds the Velocity component within an analytic that corresponds to a
 * GeoEvent component, correlating by sanitized label.
 */
export function findVelocityComponentForGeoEvent(
  model: VelocityModel,
  analytic: VelocityRealtimeAnalytic,
  geoEventLabel: string,
): VelocityComponentView | undefined {
  const target = sanitizeVelocityLabel(geoEventLabel).sanitized;
  return listVelocityComponents(model, analytic).find((c) => c.label === target);
}

/**
 * Builds the curated "Velocity Configuration" table for a single GeoEvent
 * component, or `undefined` when no corresponding Velocity component exists
 * (e.g. an unmapped component absent from the generated configuration).
 */
export function buildVelocityComponentTable(
  model: VelocityModel,
  analytic: VelocityRealtimeAnalytic,
  geoEventLabel: string,
): DocumentTable | undefined {
  const view = findVelocityComponentForGeoEvent(model, analytic, geoEventLabel);
  if (!view) return undefined;
  const rows = curateComponentRows(view);
  if (rows.length === 0) return undefined;
  return {
    title: `Velocity Configuration — ${velocityKindLabel(view.kind)}: ${view.label}`,
    rows,
  };
}

/**
 * Builds curated tables for Velocity components in an analytic that have NO
 * direct GeoEvent origin — tools/sources Velocity requires that the source
 * configuration did not contain (e.g. an injected Map Fields tool, or a
 * processor split out into a separate data source). `geoEventLabels` is the
 * set of (unsanitized) GeoEvent component labels belonging to the service.
 */
export function buildAdditionalVelocityComponentTables(
  model: VelocityModel,
  analytic: VelocityRealtimeAnalytic,
  geoEventLabels: Iterable<string>,
): DocumentTable[] {
  const originLabels = new Set<string>();
  for (const label of geoEventLabels) {
    originLabels.add(sanitizeVelocityLabel(label).sanitized);
  }

  const tables: DocumentTable[] = [];
  for (const view of listVelocityComponents(model, analytic)) {
    // Feeds always correspond to a GeoEvent input; never treat them as injected.
    if (view.kind === 'feed') continue;
    if (originLabels.has(view.label)) continue;
    const rows = curateComponentRows(view);
    const title = `${velocityKindLabel(view.kind)}: ${view.label} (${view.velocityName})`;
    tables.push({ title, rows });
  }
  return tables;
}

// --- internal helpers -------------------------------------------------------

function feedRefToView(model: VelocityModel, feedRef: AnalyticFeedRef): VelocityComponentView {
  const feedItem = model.feedsById.get(feedRef.id);
  // The full feed configuration (connection details, etc.) lives on the
  // top-level feed item; the in-analytic ref only carries layout + name.
  const properties = feedItem?.feed?.properties ?? {};
  return {
    kind: 'feed',
    velocityName: feedItem?.feed?.name || feedRef.name || 'feed',
    label: feedRef.label || feedItem?.label || '',
    properties,
  };
}

function sourceToView(source: AnalyticSource): VelocityComponentView {
  return {
    kind: 'source',
    velocityName: source.name || 'data-source',
    label: source.label || '',
    properties: source.properties || {},
  };
}

function toolToView(tool: AnalyticTool): VelocityComponentView {
  return {
    kind: 'tool',
    velocityName: tool.name || 'tool',
    label: tool.label || '',
    properties: tool.properties || {},
  };
}

function outputToView(output: AnalyticOutput): VelocityComponentView {
  return {
    kind: 'output',
    velocityName: output.name || 'output',
    label: output.label || '',
    properties: output.properties || {},
  };
}

function velocityKindLabel(kind: VelocityComponentView['kind']): string {
  switch (kind) {
    case 'feed':
      return 'Feed';
    case 'source':
      return 'Data Source';
    case 'tool':
      return 'Tool';
    case 'output':
      return 'Output';
    default:
      return 'Component';
  }
}

/**
 * Produces table rows for a Velocity component. Every configured property with
 * a non-empty value is surfaced (using its curated human label when known, or a
 * prettified key otherwise) so the Velocity view shows the full parameter set —
 * matching the depth of the GeoEvent-side tables — rather than only a curated
 * slice. Pure layout/runtime scaffolding (see `SKIP_KEYS`) is still omitted.
 */
function curateComponentRows(view: VelocityComponentView): DocumentTableRow[] {
  const rows: DocumentTableRow[] = [];

  for (const [key, rawValue] of Object.entries(view.properties)) {
    if (SKIP_KEYS.has(key)) continue;

    // Field calculations are an array of { fieldName, expression } — expand
    // each into its own row so Arcade expressions are individually readable.
    if (isFieldCalculations(key, rawValue)) {
      for (const calc of rawValue as Array<Record<string, unknown>>) {
        const fieldName = typeof calc.fieldName === 'string' && calc.fieldName ? calc.fieldName : 'field';
        const expr = typeof calc.expression === 'string' ? calc.expression : String(calc.expression ?? '');
        if (expr.trim().length > 0) {
          rows.push({ parameter: `Field Calculation: ${fieldName}`, value: expr });
        }
      }
      continue;
    }

    // Schema field mappings are `{ mappings: [{ name, expression, dataType, … }] }`.
    // Expand each field into its own row so a wide schema stays readable and no
    // single cell aggregates the whole mapping set (which can exceed the
    // per-cell text cap enforced by the render-model schema).
    if (isSchemaFieldMappings(key, rawValue)) {
      const mappings = (rawValue as { mappings: Array<Record<string, unknown>> }).mappings;
      for (const m of mappings) {
        const name = typeof m.name === 'string' && m.name ? m.name : 'field';
        const expr = typeof m.expression === 'string' ? m.expression.trim() : String(m.expression ?? '').trim();
        const dataType = typeof m.dataType === 'string' && m.dataType ? m.dataType : '';
        const value = expr.length > 0
          ? (dataType ? `${expr}  (${dataType})` : expr)
          : (dataType || '(no expression)');
        rows.push({ parameter: `Field Mapping: ${name}`, value });
      }
      continue;
    }

    const formatted = formatVelocityValue(rawValue);
    if (formatted.length === 0) continue;

    rows.push({ parameter: velocityParamLabels[key] || prettifyKey(key), value: formatted });
  }

  return rows;
}

function isFieldCalculations(key: string, value: unknown): boolean {
  return key.endsWith('.fieldCalculations') && Array.isArray(value);
}

function isSchemaFieldMappings(key: string, value: unknown): boolean {
  return key.endsWith('.schemaFieldMappings')
    && typeof value === 'object' && value !== null
    && Array.isArray((value as { mappings?: unknown }).mappings);
}

function formatVelocityValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return '';
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  if (typeof value === 'object') {
    const keys = Object.keys(value as Record<string, unknown>);
    if (keys.length === 0) return '';
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

/** Fallback label for an unlisted property key (strips the component prefix). */
function prettifyKey(key: string): string {
  const stripped = key.includes('.') ? key.slice(key.indexOf('.') + 1) : key;
  // Split camelCase and underscores/dashes into words, then title-case.
  const words = stripped
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/);
  return words.map((w) => (w.length > 0 ? w[0]!.toUpperCase() + w.slice(1) : w)).join(' ');
}
